import type { ReactNode } from 'react';
import type { Library, User } from '../api.ts';
import {
  FilmIcon,
  FolderIcon,
  HomeIcon,
  LogOutIcon,
  MusicIcon,
  PlusIcon,
  ShieldIcon,
  TvIcon,
} from '../Icon.tsx';
import type { Route } from '../route.ts';
import { BrandMark } from './BrandMark.tsx';

/**
 * Kenar çubuğu — masaüstü birincil gezinme.
 *
 * ## Mobil neden burada değil?
 *
 * Kenar çubuğu 1024 px altında gizlenir. Mobilde gezinme **alt bar**da
 * yaşar (`BottomNav.tsx`); mobil kütüphane gezinmesi ise tam ekran
 * "Kütüphaneler" sayfasındadır (`LibrariesScreen.tsx`). Bu üç yüzey aynı
 * rotaları gösterir ama her biri o ekrana özel modeldir.
 *
 * ## Karmaşıklık notu
 *
 * Etkinlik hesabı `navState()` içine, gezinme öğeleri `SidebarPrimaryNav`
 * bileşenine taşındı: bileşen, "hangi sekme etkin" sorusunun koordinasyonuyla
 * değil, düzeniyle ilgilenir.
 */
export function Sidebar({
  currentRoute,
  libraries,
  user,
  onNavigate,
  onLogout,
  onAddLibrary,
}: {
  currentRoute: Route;
  libraries: Library[] | null;
  user: User;
  onNavigate: (route: Route) => void;
  onLogout: () => void;
  onAddLibrary?: (() => void) | undefined;
}): ReactNode {
  const nav = navState(currentRoute);
  const canAddLibrary = user.role === 'admin' && Boolean(onAddLibrary);

  return (
    <aside className="sidebar" aria-label="Ana Menü">
      <div className="sidebar__brand">
        <button
          type="button"
          className="sidebar__brand-btn"
          onClick={() => onNavigate({ name: 'home' })}
        >
          <BrandMark size={32} />
          <span className="sidebar__brand-text">pbfork</span>
        </button>
      </div>

      <nav className="sidebar__nav">
        <div className="sidebar__section">
          <SidebarPrimaryNav nav={nav} onNavigate={onNavigate} />
          {user.role === 'admin' && (
            <button
              type="button"
              className={`sidebar__nav-item ${currentRoute.name === 'yonetim' ? 'is-active' : ''}`}
              aria-current={currentRoute.name === 'yonetim' ? 'page' : undefined}
              onClick={() => onNavigate({ name: 'yonetim' })}
            >
              <span className="sidebar__nav-icon" aria-hidden="true">
                <ShieldIcon size={19} />
              </span>
              <span className="sidebar__nav-label">Yönetim</span>
            </button>
          )}
        </div>

        <div className="sidebar__section">
          <div className="sidebar__section-header">
            <span className="sidebar__section-title">Kütüphaneler</span>
            {canAddLibrary && (
              <button
                type="button"
                className="sidebar__add-library-btn"
                onClick={onAddLibrary}
                title="Yeni Kütüphane Ekle"
                aria-label="Yeni Kütüphane Ekle"
              >
                <PlusIcon size={14} />
              </button>
            )}
          </div>

          {libraries && libraries.length > 0 ? (
            <SidebarLibraryList
              libraries={libraries}
              activeLibraryId={nav.libraryId}
              onOpenLibrary={(libraryId) => onNavigate({ name: 'library', libraryId })}
            />
          ) : (
            <div className="sidebar__empty-libs">Henüz kütüphane eklenmedi</div>
          )}

          {canAddLibrary && (
            <button type="button" className="sidebar__add-library-action" onClick={onAddLibrary}>
              <PlusIcon size={15} />
              <span>Kütüphane Ekle</span>
            </button>
          )}
        </div>
      </nav>

      <div className="sidebar__footer">
        <button
          type="button"
          className={`sidebar__user-btn ${nav.account ? 'is-active' : ''}`}
          aria-current={nav.account ? 'page' : undefined}
          onClick={() => onNavigate({ name: 'hesap' })}
          title="Hesap ve Sistem Durumu"
        >
          <div className="sidebar__avatar" aria-hidden="true">
            {(user.displayName || user.username).charAt(0).toUpperCase()}
          </div>
          <div className="sidebar__user-info">
            <span className="sidebar__user-name">{user.displayName || user.username}</span>
            <span className="sidebar__user-role">
              {user.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}
            </span>
          </div>
        </button>

        <button
          type="button"
          className="sidebar__logout-btn"
          onClick={onLogout}
          title="Çıkış Yap"
          aria-label="Çıkış Yap"
        >
          <LogOutIcon size={18} />
        </button>
      </div>
    </aside>
  );
}

/** Kenar çubuğundaki etkin sekmelerin durumu. */
interface NavState {
  home: boolean;
  account: boolean;
  /** Canlı TV ekranı etkin mi? */
  liveTv: boolean;
  /** Etkin kütüphane (kütüphane ekranındaysa). */
  libraryId: string | undefined;
}

/**
 * Etkin sekmeleri hesaplar.
 *
 * ## Neden tür kısayolları kaldırıldı? (Tasarım V4, Öneri 1)
 *
 * "Filmler / Diziler / Müzikler" hem birincil gezinmede hem "Kütüphaneler"
 * listesinde görünüyordu. Bir kütüphane rotasında iki öğe birden
 * `aria-current="page"` alıyor, kullanıcı hangisinin gerçek hedef olduğunu
 * anlayamıyordu. Artık **tek gezinme kaynağı** kütüphane listesidir; "Ana
 * Sayfa" ve "Hesap" yüzeyleri ayrı kalır. Mobil alt bar (ADR-008) ayrı bir
 * model olarak korunur.
 */
function navState(currentRoute: Route): NavState {
  return {
    home: currentRoute.name === 'home',
    account: currentRoute.name === 'hesap',
    liveTv: currentRoute.name === 'canli-tv',
    libraryId: currentRoute.name === 'library' ? currentRoute.libraryId : undefined,
  };
}

/**
 * Üst gezinme: yalnız "Ana Sayfa".
 *
 * Tür kısayolları (Filmler / Diziler / Müzikler) kaldırıldı: aynı hedefler
 * aşağıdaki "Kütüphaneler" listesinde zaten var. Tek kaynak, tek etkin
 * durum (Tasarım V4, Öneri 1).
 */
function SidebarPrimaryNav({
  nav,
  onNavigate,
}: {
  nav: NavState;
  onNavigate: (route: Route) => void;
}): ReactNode {
  return (
    <>
      <button
        type="button"
        className={`sidebar__nav-item ${nav.home ? 'is-active' : ''}`}
        aria-current={nav.home ? 'page' : undefined}
        onClick={() => onNavigate({ name: 'home' })}
      >
        <span className="sidebar__nav-icon" aria-hidden="true">
          <HomeIcon size={19} />
        </span>
        <span className="sidebar__nav-label">Ana Sayfa</span>
      </button>

      {/* Canlı TV: kütüphaneden ayrı bir içerik kaynağı (M3U listeleri). */}
      <button
        type="button"
        className={`sidebar__nav-item ${nav.liveTv ? 'is-active' : ''}`}
        aria-current={nav.liveTv ? 'page' : undefined}
        onClick={() => onNavigate({ name: 'canli-tv' })}
      >
        <span className="sidebar__nav-icon" aria-hidden="true">
          <TvIcon size={19} />
        </span>
        <span className="sidebar__nav-label">Canlı TV</span>
      </button>
    </>
  );
}

/**
 * Kenar çubuğundaki kütüphane listesi.
 *
 * İkon seçimi kütüphane **adından** türetilir: katalog adı "Diziler" ama
 * `kind` alanı "show" olabiliyor; ikisi birlikte değerlendirilir.
 */
function SidebarLibraryList({
  libraries,
  activeLibraryId,
  onOpenLibrary,
}: {
  libraries: Library[];
  activeLibraryId: string | undefined;
  onOpenLibrary: (libraryId: string) => void;
}): ReactNode {
  return (
    <>
      {libraries.map((lib) => {
        const isActive = activeLibraryId === lib.id;
        const isMusic =
          lib.name.toLowerCase().includes('müzik') ||
          lib.name.toLowerCase().includes('muzik') ||
          lib.name.toLowerCase().includes('music');
        return (
          <button
            type="button"
            key={lib.id}
            className={`sidebar__nav-item ${isActive ? 'is-active' : ''}`}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => onOpenLibrary(lib.id)}
          >
            <span className="sidebar__nav-icon" aria-hidden="true">
              {isMusic ? (
                <MusicIcon size={18} />
              ) : lib.kind === 'movie' ? (
                <FilmIcon size={18} />
              ) : lib.kind === 'show' ? (
                <TvIcon size={18} />
              ) : (
                <FolderIcon size={18} />
              )}
            </span>
            <span className="sidebar__nav-label">{lib.name}</span>
            {lib.layer === 'cold' && (
              <span className="sidebar__item-badge" title="Soğuk Katman (Uzak)">
                Uzak
              </span>
            )}
          </button>
        );
      })}
    </>
  );
}
